<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  title: string;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  eyebrow?: string;
  description?: string;
  showLine?: boolean;
  headingClass?: string;
  maxWidth?: string;
  containerMargin?: string;
  wrapperClass?: string;
  eyebrowClass?: string;
  descriptionClass?: string;
  lineWidth?: string;
  textAlign?: 'left' | 'center' | 'right';
}

const props = withDefaults(defineProps<Props>(), {
  headingLevel: 'h2',
  showLine: true,
  headingClass: undefined,
  maxWidth: '',
  containerMargin: 'mx-8 mb-8 lg:mx-32 lg:mb-13',
  wrapperClass: 'wrapper',
  eyebrowClass: undefined,
  descriptionClass: undefined,
  lineWidth: 'w-[100px]',
  textAlign: 'left',
  eyebrow: undefined,
  description: undefined,
});

const containerClasses = computed(() => {
  const classes = [props.maxWidth, props.containerMargin];

  if (props.wrapperClass) {
    classes.push(props.wrapperClass);
  }

  return classes.join(' ');
});

const eyebrowContainerClasses = computed(() => {
  const baseClasses = ['flex', 'items-center', 'mb-7.5'];

  if (props.textAlign === 'center') {
    baseClasses.push('justify-center');
  } else if (props.textAlign === 'right') {
    baseClasses.push('justify-end');
  }

  return baseClasses.join(' ');
});

const eyebrowTextClasses = computed(() => {
  const baseClasses = ['text-sm', 'text-grey'];

  if (props.eyebrowClass) {
    return `${baseClasses.join(' ')} ${props.eyebrowClass}`;
  }

  return baseClasses.join(' ');
});

const headingClasses = computed(() => {
  if (props.headingClass) {
    return props.headingClass;
  }

  const defaultClasses = {
    h1: 'h1-styling',
    h2: 'h2-styling',
    h3: 'h3-styling',
    h4: 'h4-styling',
    h5: 'h5-styling',
    h6: 'h6-styling',
  };

  return defaultClasses[props.headingLevel];
});

const descriptionClasses = computed(() => {
  const baseClasses = ['mt-7.5', 'text-grey', 'text-body'];

  if (props.descriptionClass) {
    return `${baseClasses.join(' ')} ${props.descriptionClass}`;
  }

  return baseClasses.join(' ');
});

const lineClasses = computed(() => {
  return ['h-px', props.lineWidth, 'mr-5', 'bg-grey'].join(' ');
});

const shouldShowLine = computed(() => {
  return props.eyebrow && props.showLine;
});

const contentAlignmentClass = computed(() => {
  const alignmentClasses = {
    left: '',
    center: 'text-center',
    right: 'text-right',
  };

  return alignmentClasses[props.textAlign];
});
</script>

<template>
  <div :class="containerClasses">
    <div v-if="eyebrow" :class="eyebrowContainerClasses">
      <div v-if="shouldShowLine" :class="lineClasses"></div>
      <span :class="eyebrowTextClasses">{{ eyebrow }}</span>
    </div>

    <component :is="headingLevel" :class="[headingClasses, contentAlignmentClass]">
      {{ title }}
    </component>

    <p v-if="description" :class="[descriptionClasses, contentAlignmentClass]">
      {{ description }}
    </p>
  </div>
</template>
